import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { CreditCard, ExternalLink, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loading } from "@/components/common/Loading";
import { useAuth } from "@/context/AuthContext";
import { tierLabel } from "@/lib/tiers";

const fmtDate = (ts) => {
  if (!ts) return null;
  try {
    return format(new Date(ts * 1000), "MMMM d, yyyy");
  } catch {
    return null;
  }
};

const STATUS_LABELS = {
  active: { label: "Active", cls: "bg-teal-50 text-teal-700 hover:bg-teal-50" },
  trialing: { label: "Trial", cls: "bg-teal-50 text-teal-700 hover:bg-teal-50" },
  past_due: { label: "Past due", cls: "bg-amber-50 text-amber-700 hover:bg-amber-50" },
  canceled: { label: "Canceled", cls: "bg-secondary text-muted-foreground hover:bg-secondary" },
};

export default function Billing() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [portalBusy, setPortalBusy] = useState(false);
  const { data: status, isLoading } = useQuery({
    queryKey: ["billing-status"],
    queryFn: async () => (await api.get("/billing/status")).data,
  });

  // Returning from the Stripe portal (e.g. after a cancellation) -> force a live
  // re-pull so the dashboard reflects the change even without a webhook.
  useEffect(() => {
    if (params.get("billing") === "updated") {
      qc.invalidateQueries({ queryKey: ["billing-status"] });
      params.delete("billing");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) return <Loading />;

  const tier = status?.tier || user?.tier || "standard";
  const hasSub = !!status?.has_subscription;
  const hasBilling = hasSub || !!status?.subscription_status;
  const interval =
    status?.interval === "year" ? "Annual" : status?.interval === "month" ? "Monthly" : "—";
  const renewDate = fmtDate(status?.current_period_end);
  const cancelDate = fmtDate(status?.cancel_at) || renewDate;
  const scheduledTier = status?.scheduled_tier;
  const scheduledDate = fmtDate(status?.scheduled_effective);
  const cancelAtEnd = !!status?.cancel_at_period_end;
  const st = STATUS_LABELS[status?.subscription_status] || null;

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open the billing portal.");
      setPortalBusy(false);
    }
  };

  return (
    <div className="pb-16" data-testid="billing-page">
      <div className="mb-6">
        <Link
          to="/dashboard/account"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary"
          data-testid="billing-back"
        >
          <ArrowLeft className="h-4 w-4" /> Account settings
        </Link>
        <h1 className="mt-2 text-2xl font-[680] tracking-tight text-foreground sm:text-3xl">Billing</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage your subscription, payment method, and invoices.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="p-6" data-testid="billing-subscription-card">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-[640] text-foreground">Current subscription</h2>
            {st && (
              <Badge className={st.cls} data-testid="billing-status-badge">
                {st.label}
              </Badge>
            )}
          </div>

          <dl className="mt-4 space-y-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Plan</dt>
              <dd className="font-[600] text-foreground" data-testid="billing-tier">
                {tierLabel(tier)}
              </dd>
            </div>
            {hasSub && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Billing</dt>
                <dd className="font-[560] text-foreground" data-testid="billing-interval">
                  {interval}
                </dd>
              </div>
            )}
            {hasSub && renewDate && !cancelAtEnd && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Renews on</dt>
                <dd className="font-[560] text-foreground" data-testid="billing-renew-date">
                  {renewDate}
                </dd>
              </div>
            )}
            {hasSub && cancelDate && cancelAtEnd && (
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Cancels on</dt>
                <dd className="font-[600] text-amber-700" data-testid="billing-cancel-date">
                  {cancelDate}
                </dd>
              </div>
            )}
          </dl>

          {cancelAtEnd && cancelDate && (
            <p
              className="mt-4 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-800"
              data-testid="billing-cancel-notice"
            >
              Your subscription is set to cancel on{" "}
              <span className="font-[600]">{cancelDate}</span>. You'll move to the free VIP plan then.
            </p>
          )}
          {!cancelAtEnd && scheduledTier && scheduledDate && (
            <p
              className="mt-4 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-800"
              data-testid="billing-scheduled-notice"
            >
              Scheduled change: you'll move to{" "}
              <span className="font-[600]">{tierLabel(scheduledTier)}</span> on{" "}
              <span className="font-[600]">{scheduledDate}</span>.
            </p>
          )}

          {!hasBilling && (
            <p className="mt-4 text-sm text-muted-foreground" data-testid="billing-free-note">
              {tier === "standard"
                ? "You're on the free VIP plan — no billing details on file."
                : "No active subscription on file — your plan isn't billed through Stripe."}
            </p>
          )}

          <div className="mt-6">
            <Link to="/dashboard/plans">
              <Button variant="secondary" data-testid="billing-change-plan">
                Change plan
              </Button>
            </Link>
          </div>
        </Card>

        <Card className="p-6" data-testid="billing-portal-card">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-[640] text-foreground">Payment &amp; invoices</h2>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Update your card, download invoices, or cancel your subscription in the secure Stripe
            billing portal.
          </p>
          {hasBilling ? (
            <Button
              className="mt-5"
              onClick={openPortal}
              disabled={portalBusy}
              data-testid="billing-portal-button"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              {portalBusy ? "Opening…" : "Open billing portal"}
            </Button>
          ) : (
            <Link to="/dashboard/plans">
              <Button className="mt-5" data-testid="billing-choose-plan">
                Choose a plan
              </Button>
            </Link>
          )}
        </Card>
      </div>
    </div>
  );
}
